import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import 'lightgallery/scss/lightgallery.scss';
import LightGallery from 'lightgallery/react';
import { useParams } from 'react-router-dom';
import ApiService from '../../services/api-service';
import './orb-details-component.scss';
import OrbMetadata from '../../models/orb-metadata-model';

type NamedRowProps = {
  name: string;
  value: string | number | undefined;
};

function NamedRow({ name, value }: NamedRowProps) {
  if (value) {
    return (
      <>
        <Row>
          <Col sm={4}>{name}</Col>
          <Col sm={8}>{value}</Col>
        </Row>
        <hr className="my-2" />
      </>
    );
  }

  return null;
}

export default function OrbDetailsComponent(): JSX.Element {
  const { orbId } = useParams();

  const [orbMetadata, setOrbMetadata] = useState<OrbMetadata>();
  const [vintage, setVintage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lightGallery = useRef<any>(null);

  useEffect(() => {
    const apiService = new ApiService();
    const orbIdWithoutPrefix = orbId?.includes('-') ? orbId.split('-')?.[1] : orbId;
    apiService.getCardMetadataAsync(orbIdWithoutPrefix || '').then((orbDetails) => {
      setOrbMetadata(orbDetails?.data);
      setImageUrl(orbDetails?.data?.imgURL);
    });

    return () => {
      setOrbMetadata(undefined);
    };
  }, []);

  useEffect(() => {
    setImageUrl((vintage ? orbMetadata?.vintageImgUrl : orbMetadata?.imgURL) || '');
  }, [vintage]);

  useEffect(() => {
    // refreshing image after vintage button click
    lightGallery?.current?.refresh();
  }, [imageUrl]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onInit = useCallback((detail: any) => {
    if (detail) {
      lightGallery.current = detail.instance;
    }
  }, []);

  return (
    <>
      <h3 className="pb-2 pt-4">{orbMetadata?.name}</h3>
      <Card>
        <Row className="media pb-4 justify-content-center">
          {imageUrl ? (
            <>
              <Col sm={4}>
                <LightGallery
                  licenseKey={process.env.REACT_APP_LIGHTGALLERY_API_KEY}
                  thumbHeight="5rem"
                  startAnimationDuration={0}
                  speed={0}
                  toggleThumb={false}
                  backdropDuration={200}
                  slideEndAnimation
                  controls={false}
                  counter={false}
                  download={false}
                  onInit={onInit}
                >
                  <a href={imageUrl} data-src={imageUrl}>
                    <img src={imageUrl} className="card-img px-2" alt={orbMetadata?.name} />
                  </a>
                </LightGallery>
              </Col>
              <Col sm={6} className="px-3">
                <div className="title-line pt-2 mb-3">
                  <h4>{orbMetadata?.name}</h4>
                  {orbMetadata?.moongaId && <h3 className="title-line">Spells of Genesis</h3>}
                </div>
                {orbMetadata?.imgURL && orbMetadata?.vintageImgUrl && (
                  <div>
                    <NamedRow name="Name" value={orbMetadata.name} />
                    <NamedRow name="Moonga Id" value={orbMetadata.moongaId} />
                    <NamedRow name="Id" value={orbMetadata.unid} />
                    <NamedRow name="Evolution rank" value={orbMetadata.evolutionRank} />
                    <NamedRow name="Rarity" value={orbMetadata.rarity} />
                    <NamedRow name="Conterparty" value={orbMetadata.contracts?.counterparty?.id} />
                    <NamedRow name="Ethereum" value={orbMetadata.contracts?.ethereum?.id} />
                    <NamedRow name="Klaytn" value={orbMetadata.contracts?.klaytn?.id} />
                    <Row>
                      <Col sm={4}>Vintage</Col>
                      <Col sm={8}>
                        <Form.Check
                          type="switch"
                          id={orbMetadata.name}
                          label=""
                          checked={vintage}
                          onChange={() => setVintage(!vintage)}
                        />
                      </Col>
                    </Row>
                  </div>
                )}
              </Col>
            </>
          ) : (
            <h3 className="pt-4 ps-4">The data is missing for that Orb</h3>
          )}
        </Row>
      </Card>
    </>
  );
}
